import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export const Route = createFileRoute("/auth")({
  head: () => ({ meta: [{ title: "Sign in — Creed Car Rental" }] }),
  component: Auth,
});

function Auth() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signin") {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success("Signed in");
        navigate({ to: "/admin" });
      } else {
        const { error } = await supabase.auth.signUp({
          email, password,
          options: { emailRedirectTo: window.location.origin },
        });
        if (error) throw error;
        toast.success("Account created. You can now sign in.");
        setMode("signin");
      }
    } catch (err: any) {
      toast.error(err.message || "Authentication failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-cream">
      <SiteHeader />

      {/* Hero header */}
      <section className="relative isolate overflow-hidden border-b border-border/60 bg-espresso py-16 md:py-20">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(60% 80% at 85% 20%, oklch(0.72 0.15 65 / 0.35), transparent 60%), radial-gradient(50% 70% at 10% 90%, oklch(0.72 0.15 65 / 0.18), transparent 60%)",
          }}
          aria-hidden
        />
        <svg
          className="pointer-events-none absolute -right-24 -top-24 h-[520px] w-[520px] text-accent/20"
          viewBox="0 0 600 600"
          fill="none"
          aria-hidden
        >
          <circle cx="300" cy="300" r="220" stroke="currentColor" strokeWidth="1" />
          <circle cx="300" cy="300" r="160" stroke="currentColor" strokeWidth="1" />
          <circle cx="300" cy="300" r="100" stroke="currentColor" strokeWidth="1" />
        </svg>
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <h1 className="font-display text-4xl text-cream md:text-5xl">{mode === "signin" ? "Welcome back" : "Create account"}</h1>
          <p className="mt-3 text-cream/70">Owner / admin access</p>
        </div>
      </section>

      <div className="mx-auto max-w-md px-6 py-16">
        <form onSubmit={submit} className="mt-8 space-y-4 rounded-lg border border-border bg-card p-6">
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={email} onChange={e => setEmail(e.target.value)} required /></div>
          <div className="space-y-1.5"><Label>Password</Label><Input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} /></div>
          <Button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90">
            {loading ? "…" : mode === "signin" ? "Sign in" : "Sign up"}
          </Button>
        </form>
        <button type="button" onClick={() => setMode(mode === "signin" ? "signup" : "signin")} className="mt-4 block w-full text-center text-sm text-espresso/70 hover:text-accent">
          {mode === "signin" ? "Need an account? Sign up" : "Have an account? Sign in"}
        </button>
        <div className="mt-6 text-center"><Link to="/" className="text-sm text-muted-foreground hover:text-accent">← Back home</Link></div>
      </div>
      <SiteFooter />
    </div>
  );
}
